// Global
import React from 'react';
import { Item } from '@sitecore-content-sdk/nextjs';

// Local
import { Navigation } from '@generated/Navigation/Footer.model';
import { Footer } from './Footer';
import {
  PureFooterMenu,
  PureFooterColumn,
  PureFooterLink,
  PureFooterBottomBar,
  PureFooterBottomBarLink,
  RegionSelectorData,
  PureNewsLetter,
} from './types';

import PlainTextWrapper from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';

import useDictionary from 'lib/hooks/useDictionary';
import { SvgIcon } from 'components/helpers/SvgIcon';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';

import megaFooterStyles from './MegaFooter/MegaFooter.module.scss';
import footerBottomBarStyles from './FooterBottomBar/FooterBottomBar.module.scss';

export const Default = (props: Navigation.Footer.Footer_Component) => {
  const { getDictionaryValue } = useDictionary();
  // Transform Sitecore data to PureFooterMenu structure
  const transformToPureFooterMenu = (): PureFooterMenu => {
    const columns: PureFooterColumn[] = [];

    props.fields?.footerLinkSections?.forEach((section: Item & Navigation.Footer.FooterSection) => {
      const links: PureFooterLink[] = [];
      const sectionTitle = section.fields?.footerSectionTitle?.value;

      section.fields?.footerLinks?.forEach((footerLink: Item & Navigation.Footer.FooterLink) => {
        if (
          footerLink.fields?.footerLinkUrl?.value?.href &&
          footerLink.fields?.footerLinkText?.value
        ) {
          links.push({
            link: (
              <LinkWrapper
                showLinkTextWithChildrenPresent={false}
                ctaStyle="none"
                ctaIconSize="sm"
                field={footerLink.fields.footerLinkUrl}
                linkContext="footer"
                analyticsHeader={sectionTitle}
                analyticsClickText={footerLink?.fields?.footerLinkText?.value}
              >
                <PlainTextWrapper
                  className={megaFooterStyles.linkText}
                  field={footerLink?.fields?.footerLinkText}
                  tag="span"
                />
              </LinkWrapper>
            ),
          });
        }
      });

      if (links.length > 0) {
        columns.push({
          headerText: <PlainTextWrapper field={section.fields?.footerSectionTitle} />,
          links,
        });
      }
    });

    return {
      mainNavigationLabel: getDictionaryValue(
        'Navigation.Footer.Arialabel.MainNavAriaLabel',
        'Footer'
      ),
      columns,
    };
  };

  // Transform: Bottom bar
  const transformToPureFooterBottomBar = (): PureFooterBottomBar | undefined => {
    const bottomBarSection = props.fields?.footerBottomBar as unknown as Item &
      Navigation.Footer.FooterSection;

    if (!bottomBarSection?.fields?.footerLinks) {
      return undefined;
    }

    const links: PureFooterBottomBarLink[] = [];

    bottomBarSection.fields.footerLinks.forEach(
      (
        footerLink: Item &
          Navigation.Footer.FooterLink &
          Navigation.Footer.CustomizeCookieSettings &
          Navigation.Footer.YourPrivacyChoice &
          Navigation.Footer.AdChoicesLink
      ) => {
        let linkType: 'default' | 'privacyChoice' | 'cookieSettings' | 'adChoices';
        if ('showYourPrivacyChoice' in footerLink?.fields) {
          linkType = 'privacyChoice';
        } else if ('showCustomizeCookieSettings' in footerLink?.fields) {
          linkType = 'cookieSettings';
        } else if ('showAdChoicesLink' in footerLink?.fields) {
          linkType = 'adChoices';
        } else {
          linkType = 'default';
        }

        if (linkType === 'default') {
          links.push({
            link: (
              <LinkWrapper
                showLinkTextWithChildrenPresent={false}
                ctaStyle="none"
                field={footerLink.fields.footerLinkUrl}
                ctaIconSize="sm"
                linkContext="footer"
                analyticsHeader={footerLink?.fields?.footerLinkText?.value}
                analyticsClickText={footerLink?.fields?.footerLinkText?.value}
              >
                <PlainTextWrapper
                  field={footerLink?.fields?.footerLinkText}
                  tag="span"
                  className={footerBottomBarStyles.linkText}
                />
              </LinkWrapper>
            ),
            linkType,
          });
        } else if (
          linkType === 'privacyChoice' &&
          footerLink.fields?.showYourPrivacyChoice?.value
        ) {
          links.push({
            link: (
              <CtaLink
                url={getDictionaryValue('Navigation.Footer.YourPrivacyChoice.YourPrivacyChoiceUrl')}
                target="_blank"
                suppressNewTabIcon
                srOnlyText={getDictionaryValue(
                  'Navigation.Footer.YourPrivacyChoice.YourPrivacyChoiceLinkScreenReaderText'
                )}
                ctaStyle="none"
                linkContext="footer"
                analyticsHeader={getDictionaryValue(
                  'Navigation.Footer.YourPrivacyChoice.YourPrivacyChoiceLinkText'
                )}
                analyticsClickText={getDictionaryValue(
                  'Navigation.Footer.YourPrivacyChoice.YourPrivacyChoiceLinkText'
                )}
              >
                <span className={footerBottomBarStyles.linkText}>
                  {getDictionaryValue(
                    'Navigation.Footer.YourPrivacyChoice.YourPrivacyChoiceLinkText'
                  )}
                </span>
                <SvgIcon viewBox="0 0 29 14" icon="privacy-choices" />
                <SvgIcon icon="new-tab" size="sm" />
              </CtaLink>
            ),
            linkType,
          });
        } else if (
          linkType === 'cookieSettings' &&
          footerLink.fields?.showCustomizeCookieSettings?.value
        ) {
          links.push({
            link: (
              <CtaButton
                type="button"
                ctaStyle="link-invert-underline"
                ctaComponentClass="component-site-footer-bottom-bar-cta-2-color"
                data-gmiot="gmi-onetrust-preference-link"
              >
                <SvgIcon viewBox="0 0 24 24" icon="leading" />
                <span className={footerBottomBarStyles.linkText}>
                  {getDictionaryValue(
                    'Common.OneTrustSettings.CookieSettingLabel',
                    'Customize Cookie Settings'
                  )}
                </span>
              </CtaButton>
            ),
            linkType,
          });
        } else if (linkType === 'adChoices' && footerLink.fields?.showAdChoicesLink?.value) {
          links.push({
            link: (
              <CtaLink
                url="/"
                ctaComponentClass="component-site-footer-footer-links-link"
                ctaStyle="none"
                onClick={(e) => {
                  e.preventDefault();
                  window.TRUSTeWidget?.Tab?.link();
                }}
                suppressNewTabIcon
                linkContext="footer"
                analyticsHeader={getDictionaryValue('Navigation.Footer.AdChoices', 'AdChoices')}
                analyticsClickText={getDictionaryValue('Navigation.Footer.AdChoices', 'AdChoices')}
              >
                <span className={footerBottomBarStyles.linkText}>
                  {getDictionaryValue('Navigation.Footer.AdChoices', 'AdChoices')}
                </span>
                <SvgIcon viewBox="0 0 24 25" icon="ad-choices" />
              </CtaLink>
            ),
            linkType,
          });
        }
      }
    );

    return {
      links,
      copyrightText: getDictionaryValue('Navigation.Footer.CopyRight.CopyRightText').replace(
        '{currentYear}',
        new Date().getFullYear().toString()
      ),
      bottomNavigationLabel: getDictionaryValue(
        'Navigation.Footer.Arialabel.BottomBarAriaLabel',
        'Footer utility'
      ),
    };
  };

  // Transform regionSelector data to RegionSelectorData format
  const transformRegionSelectorData = (): RegionSelectorData | undefined => {
    const regionSelector = props.fields?.regionSelector;
    if (!regionSelector) {
      return undefined;
    }

    // If it's an array, get the first item
    const selectorItem = Array.isArray(regionSelector) ? regionSelector[0] : regionSelector;

    if (!selectorItem) {
      return undefined;
    }

    return {
      fields: {
        Heading: selectorItem.fields?.heading,
        SubHeading: selectorItem.fields?.subHeading,
        CountriesList: selectorItem.fields?.countriesList || [],
      },
    };
  };

  // Transform: Newsletter (from generated model)
  const transformToPureNewsLetter = (): PureNewsLetter | undefined => {
    const newsletterItem = props.fields?.newsLetter as Item & Navigation.Footer.Newsletter;
    if (!newsletterItem?.fields) return undefined;
    const headline = newsletterItem.fields.headline?.value ? (
      <PlainTextWrapper field={newsletterItem.fields.headline} tag="span" />
    ) : undefined;
    const description = newsletterItem.fields.description?.value ? (
      <PlainTextWrapper field={newsletterItem.fields.description} tag="span" />
    ) : undefined;

    const linkField = newsletterItem.fields.newsLink;
    const cta = linkField ? { linkField } : undefined;
    if (!headline && !description && !cta) return undefined;
    return { headline, description, cta } as PureNewsLetter;
  };

  const pureFooterMenu = transformToPureFooterMenu();
  const pureFooterBottomBar = transformToPureFooterBottomBar();
  const regionSelectorData = transformRegionSelectorData();
  const pureNewsLetter = transformToPureNewsLetter();

  if (
    (pureFooterBottomBar?.links.length ?? 0) === 0 &&
    (pureFooterMenu?.columns?.length ?? 0) === 0 &&
    !pureNewsLetter
  ) {
    return null;
  }

  return (
    <Footer
      footerLogo={<ImageWrapper field={props.fields?.footerLogo} />}
      pureFooterMenu={pureFooterMenu}
      pureFooterBottomBar={pureFooterBottomBar}
      regionSelectorData={regionSelectorData}
      pureNewsLetter={pureNewsLetter}
    />
  );
};
